import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { File as SlabFile } from '@core/types/api/File/File';
import { type FileEntityType, type UploadItem, UploadItemStatus } from '@core/types/file';
import { hasValue } from '@core/utils/equality';
import { contentTypeForFileName, validateAttachmentFile } from '@core/utils/fileAttachments';
import { randomUUID } from '@core/utils/uuid';
import { useSlabMutation } from '@web/hooks/useSlabMutation';
import { ServerErrorMessage } from '@web/hooks/useSlabQuery';

const isAttachableItem = (item: UploadItem): boolean =>
  item.status === UploadItemStatus.Done && !hasValue(item.file) && hasValue(item.storageKey);

const isUploadingItem = (item: UploadItem): boolean => item.status === UploadItemStatus.Uploading;

const isPendingItem = (item: UploadItem): boolean =>
  isUploadingItem(item) || item.status === UploadItemStatus.Registering;

export type AttachAllResult = {
  attachedFiles: SlabFile[];
  failedFileNames: string[];
};

export type FileUploadsManager = {
  items: UploadItem[];
  hasAttachableItems: boolean;
  hasPendingUploads: boolean;
  isSensitive: boolean;
  setIsSensitive: (value: boolean) => void;
  addFiles: (files: File[]) => void;
  retry: (id: string) => void;
  remove: (id: string) => void;
  attachAll: (entityId: string) => Promise<AttachAllResult>;
  reset: () => void;
  cloneSourceEntityId: string | null;
  excludedCloneFileIds: string[];
  excludeCloneFile: (fileId: string) => void;
};

export type UseFileUploadsOptions = {
  entityType: FileEntityType;
  entityId: string | null;
  cloneSourceEntityId?: string | null;
  onFileAttached?: (file: SlabFile) => void;
};

type UploadFileArgs = {
  id: string;
  browserFile: File;
  contentType: string;
};

type RegisterFileArgs = {
  id: string;
  storageKey: string;
  fileName: string;
  targetEntityId: string;
  isSensitive: boolean;
};

/**
 * Owns the per-file upload pipeline (signed URL -> GCS PUT -> register), plus the
 * source files a clone drops before its own entity exists. The returned manager is
 * referentially stable, so consumers can depend on it in effects.
 */
export const useFileUploads = ({
  entityType,
  entityId,
  cloneSourceEntityId = null,
  onFileAttached,
}: UseFileUploadsOptions): FileUploadsManager => {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [excludedCloneFileIds, setExcludedCloneFileIds] = useState<string[]>([]);
  const [isSensitive, setIsSensitive] = useState(false);
  const isSensitiveRef = useRef(isSensitive);
  isSensitiveRef.current = isSensitive;
  const browserFilesRef = useRef<Map<string, File>>(new Map());

  const uploadPromisesRef = useRef<Map<string, Promise<string | null>>>(new Map());

  const registrationQueueRef = useRef<Promise<unknown>>(Promise.resolve());

  const itemsRef = useRef<UploadItem[]>(items);
  itemsRef.current = items;

  const { mutateAsync: requestSignedUrlAsync } = useSlabMutation('POST signed url for file');
  const { mutateAsync: registerUploadedFileAsync } = useSlabMutation('POST file');

  const updateItem = useCallback((id: string, patch: Partial<UploadItem>): void => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  const { mutateAsync: registerFileMutateAsync } = useMutation({
    mutationFn: ({
      storageKey,
      fileName,
      targetEntityId,
      isSensitive: itemIsSensitive,
    }: RegisterFileArgs) =>
      registerUploadedFileAsync({
        args: {
          body: {
            storageKey,
            fileName,
            entityType,
            entityId: targetEntityId,
            isSensitive: itemIsSensitive,
          },
        },
      }),
    onSuccess: (file, { id }) => {
      updateItem(id, { status: UploadItemStatus.Done, file });
      onFileAttached?.(file);
    },
    onError: (error, { id }) =>
      updateItem(id, {
        status: UploadItemStatus.Failed,
        errorMessage: ServerErrorMessage(error, 'Could not save this file. Try again.'),
      }),
  });

  const registerFile = useCallback(
    (id: string, storageKey: string, fileName: string): void => {
      const queued = itemsRef.current.find((item) => item.id === id);
      if (!hasValue(queued)) {
        // Item was removed mid-upload; never register it.
        return;
      }
      const itemIsSensitive = queued.isSensitive ?? false;

      if (!hasValue(entityId)) {
        updateItem(id, { status: UploadItemStatus.Done, storageKey });
        return;
      }

      updateItem(id, { status: UploadItemStatus.Registering, storageKey });
      registrationQueueRef.current = registrationQueueRef.current.then(() => {
        // reset()/remove() may drop this item while it sits in the registration queue.
        if (!itemsRef.current.some((item) => item.id === id)) {
          return undefined;
        }
        return registerFileMutateAsync({
          id,
          storageKey,
          fileName,
          targetEntityId: entityId,
          isSensitive: itemIsSensitive,
        }).catch(() => undefined);
      });
    },
    [entityId, registerFileMutateAsync, updateItem],
  );

  const { mutateAsync: uploadFileMutationAsync } = useMutation({
    mutationFn: async ({ id, browserFile, contentType }: UploadFileArgs) => {
      const { url, storageKey } = await requestSignedUrlAsync({
        args: {
          body: {
            fileName: browserFile.name,
            contentType,
            sizeBytes: browserFile.size,
            entityType,
          },
        },
      });

      await axios.put(url, browserFile, {
        headers: { 'Content-Type': contentType },
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total ?? browserFile.size;
          const progress = total > 0 ? Math.round((progressEvent.loaded / total) * 100) : 0;
          updateItem(id, { progress });
        },
      });

      return storageKey;
    },
    onSuccess: (storageKey, { id, browserFile }) => registerFile(id, storageKey, browserFile.name),
    onError: (error, { id }) =>
      updateItem(id, {
        status: UploadItemStatus.Failed,
        errorMessage: ServerErrorMessage(error, 'Upload failed. Try again.'),
      }),
  });

  const uploadFile = useCallback(
    (id: string, browserFile: File): void => {
      updateItem(id, { status: UploadItemStatus.Uploading, progress: 0, errorMessage: undefined });

      const contentType = contentTypeForFileName(browserFile.name);
      if (!hasValue(contentType)) {
        // Should not happen: addFiles validates before queuing.
        updateItem(id, {
          status: UploadItemStatus.Failed,
          errorMessage: 'Upload failed. Try again.',
        });
        return;
      }

      uploadPromisesRef.current.set(
        id,
        uploadFileMutationAsync({ id, browserFile, contentType }).catch(() => null),
      );
    },
    [uploadFileMutationAsync, updateItem],
  );

  const addFiles = useCallback(
    (files: File[]): void => {
      const newItems: UploadItem[] = files.map((file) => {
        const id = randomUUID();
        const validationError = validateAttachmentFile(file);

        if (hasValue(validationError)) {
          return {
            id,
            fileName: file.name,
            sizeBytes: file.size,
            progress: 0,
            status: UploadItemStatus.Rejected,
            errorMessage: validationError,
            isSensitive: isSensitiveRef.current,
          };
        }

        browserFilesRef.current.set(id, file);
        return {
          id,
          fileName: file.name,
          sizeBytes: file.size,
          progress: 0,
          status: UploadItemStatus.Uploading,
          isSensitive: isSensitiveRef.current,
        };
      });

      setItems((prev) => [...prev, ...newItems]);

      newItems
        .filter((item) => item.status === UploadItemStatus.Uploading)
        .forEach((item) => {
          const browserFile = browserFilesRef.current.get(item.id);
          if (hasValue(browserFile)) {
            uploadFile(item.id, browserFile);
          }
        });
    },
    [uploadFile],
  );

  const retry = useCallback(
    (id: string): void => {
      const item = itemsRef.current.find((i) => i.id === id);
      if (!hasValue(item) || item.status !== UploadItemStatus.Failed) {
        return;
      }

      if (hasValue(item.storageKey)) {
        // Bytes already made it to GCS -- only the registration step failed.
        registerFile(id, item.storageKey, item.fileName);
        return;
      }

      const browserFile = browserFilesRef.current.get(id);
      if (hasValue(browserFile)) {
        uploadFile(id, browserFile);
      }
    },
    [registerFile, uploadFile],
  );

  const remove = useCallback((id: string): void => {
    browserFilesRef.current.delete(id);
    uploadPromisesRef.current.delete(id);
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const excludeCloneFile = useCallback((fileId: string): void => {
    setExcludedCloneFileIds((prev) => (prev.includes(fileId) ? prev : [...prev, fileId]));
  }, []);

  const reset = useCallback((): void => {
    browserFilesRef.current.clear();
    uploadPromisesRef.current.clear();
    setItems([]);
    setExcludedCloneFileIds([]);
  }, []);

  const attachAll = useCallback(
    async (targetEntityId: string): Promise<AttachAllResult> => {
      const snapshot = itemsRef.current;
      const readyItems = snapshot.filter(isAttachableItem);

      const uploadingItems = hasValue(entityId)
        ? []
        : snapshot
            .filter(isUploadingItem)
            .map((item) => ({ item, upload: uploadPromisesRef.current.get(item.id) }));

      const attachedFiles: SlabFile[] = [];
      const failedFileNames: string[] = [];

      const register = async (item: UploadItem, storageKey: string): Promise<void> => {
        // allSettled keeps the batch going when one item fails; the mutation's own
        // onSuccess/onError handle the per-item state updates.
        const [result] = await Promise.allSettled([
          registerFileMutateAsync({
            id: item.id,
            storageKey,
            fileName: item.fileName,
            targetEntityId,
            isSensitive: item.isSensitive ?? false,
          }),
        ]);

        if (result.status === 'fulfilled') {
          attachedFiles.push(result.value);
        } else {
          failedFileNames.push(item.fileName);
        }
      };

      for (const item of readyItems) {
        // eslint-disable-next-line no-await-in-loop -- registrations must run in order, one at a time
        await register(item, item.storageKey as string);
      }

      for (const { item, upload } of uploadingItems) {
        // eslint-disable-next-line no-await-in-loop -- registrations must run in order, one at a time
        const storageKey = await upload;
        if (hasValue(storageKey)) {
          // eslint-disable-next-line no-await-in-loop -- registrations must run in order, one at a time
          await register(item, storageKey);
        } else {
          // The upload itself failed; the item is already marked Failed for retry.
          failedFileNames.push(item.fileName);
        }
      }

      return { attachedFiles, failedFileNames };
    },
    [entityId, registerFileMutateAsync],
  );

  return useMemo(
    () => ({
      items,
      hasAttachableItems: items.some(isAttachableItem),
      hasPendingUploads: items.some(isPendingItem),
      isSensitive,
      setIsSensitive,
      addFiles,
      retry,
      remove,
      attachAll,
      reset,
      cloneSourceEntityId,
      excludedCloneFileIds,
      excludeCloneFile,
    }),
    [
      items,
      isSensitive,
      addFiles,
      retry,
      remove,
      attachAll,
      reset,
      cloneSourceEntityId,
      excludedCloneFileIds,
      excludeCloneFile,
    ],
  );
};
